
import { Helmet } from "react-helmet";
import HeroSection from "@/components/HeroSection";
import QuotePersonalSection from "@/components/about/QuotePersonalSection";
import QualificationsSection from "@/components/about/QualificationsSection";
import WhatSetsApartSection from "@/components/about/WhatSetsApartSection";
import PersonalNoteSection from "@/components/about/PersonalNoteSection";
import CTASection from "@/components/CTASection";
import YouTubeLink from "@/components/YouTubeLink";
import SEOMetadata from "@/components/SEOMetadata";
import { webPageStructuredData } from "@/data/structuredData";

const UeberMich = () => {
  return (
    <main>
      <SEOMetadata
        metaTitle="Über Jörg Hörmann – Coach & Strategischer Begleiter"
        metaDescription="Erfahren Sie mehr über Jörg Hörmann: Beruflicher Hintergrund, Qualifikationen und der Katalysator-Ansatz für Coaching und Führung."
        pageName="Über mich"
        primaryLink="/ueber-mich"
        title="Über Jörg Hörmann"
        description="Coach, Finanzcoach & strategischer Begleiter"
        imageUrl="/lovable-uploads/21de3789-ed78-4fd4-9bae-abb205041b23.png"
        keywords="Jörg Hörmann, Coach Bayern, Finanzcoach, Executive Coach, Katalysator-Führung"
        canonicalUrl="https://joerg-hoermann.de/ueber-mich"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(webPageStructuredData({
            name: "Über Jörg Hörmann – Coach & Strategischer Begleiter",
            description: "Erfahren Sie mehr über Jörg Hörmann: Beruflicher Hintergrund, Qualifikationen und der Katalysator-Ansatz für Coaching und Führung.",
            url: "https://joerg-hoermann.de/ueber-mich",
            aboutTopics: ["katalysatorFuehrung", "koerperintelligenz", "burnout", "selbstfuehrung"]
          }))}
        </script>
      </Helmet>

      {/* 1. Hero */}
      <HeroSection
        title="Persönliche Entwicklung & Finanzielle Klarheit"
        subtitle="Willkommen bei Jörg Hörmann"
        description="Ich begleite Menschen und Unternehmen auf ihrem Weg zu mehr Klarheit, Balance und nachhaltiger Entwicklung."
        backgroundClass="bg-joerg-50"
        imageUrl="/lovable-uploads/21de3789-ed78-4fd4-9bae-abb205041b23.png"
        portraitMode={true}
        imageObjectPosition="object-top"
        hideHeaderLogo={false}
        logoPosition="top-center"
        hideHeroLogo={true}
      />

      {/* AI-Summary / AEO Abstract */}
      <section className="sr-only" aria-label="Zusammenfassung" data-speakable="summary">
        <p>Jörg Hörmann ist Executive Coach, Finanzcoach und strategischer Begleiter mit Sitz in Roth bei Nürnberg. Seine Katalysator-Führung verbindet emotionale Intelligenz mit wissenschaftlich fundierter Methodik.</p>
      </section>

      {/* 2. Meine Geschichte + Video */}
      <section className="py-16 md:py-20 bg-background">
        <div className="container max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">Meine Geschichte</h2>
            <div className="w-16 h-1 bg-primary mx-auto my-4"></div>
            <p className="mt-3 text-muted-foreground text-lg">Die Neugier eines Kindes prägt den Weg eines Lebens</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="order-2 md:order-1 prose prose-lg max-w-none">
              <p className="text-muted-foreground leading-relaxed mb-4">
                Das Gewusel einer sechsköpfigen Familie. Ich als kleiner Junge mittendrin, auf der Suche nach meinem Bild von der Welt: 
                Mit dem Fernrohr in die Sterne geschaut, die Geheimnisse von Moos und Teichwasser unterm Mikroskop entdeckt, durch die Wälder gestreift…
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Diese <span className="font-medium text-foreground">unstillbare Neugier</span> hat meinen Lebensweg geprägt und ist bis heute die treibende Kraft hinter meiner Arbeit als Coach und Berater.
              </p>
            </div>
            <div className="order-1 md:order-2">
              <YouTubeLink
                youtubeId="7RV2epX6n2I" 
                title="Die Neugier eines Kindes"
                aspectRatio="9/16"
                className="mx-auto md:ml-auto rounded-xl overflow-hidden shadow-elevation max-w-xs md:max-w-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 3. Persönlicher Weg (gestrafft) */}
      <QuotePersonalSection />

      {/* 4. Qualifikationen (reduziert) */}
      <QualificationsSection />

      {/* 5. Was meine Arbeit prägt (konsolidiert) */}
      <WhatSetsApartSection />

      {/* 6. Persönliche Note + CTA */}
      <PersonalNoteSection />

      <CTASection
        title="Ein ruhiges Gespräch – unverbindlich und kostenfrei"
        description="Lassen Sie uns herausfinden, ob und wie ich Sie begleiten kann."
        primaryCTA={{ text: "Erstgespräch vereinbaren", link: "/kontakt" }}
        backgroundClass="bg-gradient-to-br from-[#EDF5E9] to-[#E8F1FB] text-joerg-800"
      />
    </main>
  );
};

export default UeberMich;
